/**
 * Formats a numeric amount as a currency string for any ISO 4217 currency code.
 *
 * Uses the platform's `Intl.NumberFormat` with `style: 'currency'`, which natively
 * resolves the correct currency symbol (e.g. €, $, £, ¥, R$) and decimal rules
 * for the provided locale. Use this when the currency is not known at compile
 * time (e.g. values returned by Stripe).
 *
 * @param value - Numeric amount to format.
 * @param currency - ISO 4217 currency code (e.g. `EUR`, `USD`, `GBP`, `JPY`).
 * @param locale - BCP 47 locale tag used for formatting. Defaults to `it-IT`.
 * @param options - Optional formatting overrides (e.g. `hideDecimals` to omit fractional digits).
 * @returns The formatted currency string, or the raw value if formatting fails.
 */
export type FormatAllCurrencyAmountOptions = {
  hideDecimals?: boolean;
};

export function formatAllCurrencyAmount(
  value: number,
  currency: string,
  locale: string = 'it-IT',
  options: FormatAllCurrencyAmountOptions = {},
): string {
  const { hideDecimals = false } = options;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      ...(hideDecimals && { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
    }).format(value);
  } catch {
    return `${value} ${currency}`;
  }
}
